// Client-facing proposal model built from already-computed scenario results.
// This layer projects balances and presentation metrics; it never changes the
// nondiscrimination result or fabricates actuarial funding requirements.

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const cents = (value) => Math.round((Number(value) || 0) * 100)
const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, Number(value) || 0))

export const DEFAULT_NEW_COMP_REPORT_INPUTS = Object.freeze({
  projectionYears: 10,
  marginalTaxRatePercent: 37,
  salaryGrowthPercent: 0,
  interestCreditAssumptionPercent: null,
  fundingCorridor: {
    minimum: null,
    maximum: null,
    recommended: null,
    source: '',
    asOf: '',
    note: '',
  },
  disclosures: {
    beneficiaryDesignationReviewed: false,
    spousalConsentApplies: true,
    qjsaApplies: true,
    note: '',
  },
})

export function normalizeNewCompReportInputs(value = {}) {
  const source = isObject(value) ? value : {}
  const corridor = isObject(source.fundingCorridor) ? source.fundingCorridor : {}
  const disclosures = isObject(source.disclosures) ? source.disclosures : {}
  const nullableMoney = (entry) => entry === '' || entry === null || entry === undefined
    ? null : Math.max(0, Number(entry) || 0)
  return {
    projectionYears: Math.round(clamp(source.projectionYears ?? 10, 1, 30)),
    marginalTaxRatePercent: clamp(source.marginalTaxRatePercent ?? 37, 0, 60),
    salaryGrowthPercent: clamp(source.salaryGrowthPercent ?? 0, 0, 15),
    interestCreditAssumptionPercent:
      source.interestCreditAssumptionPercent === '' || source.interestCreditAssumptionPercent === null
        || source.interestCreditAssumptionPercent === undefined
        ? null : clamp(source.interestCreditAssumptionPercent, 0, 15),
    fundingCorridor: {
      minimum: nullableMoney(corridor.minimum),
      maximum: nullableMoney(corridor.maximum),
      recommended: nullableMoney(corridor.recommended),
      source: String(corridor.source || '').trim(),
      asOf: String(corridor.asOf || '').trim(),
      note: String(corridor.note || '').trim(),
    },
    disclosures: {
      beneficiaryDesignationReviewed: disclosures.beneficiaryDesignationReviewed === true,
      spousalConsentApplies: disclosures.spousalConsentApplies !== false,
      qjsaApplies: disclosures.qjsaApplies !== false,
      note: String(disclosures.note || '').trim(),
    },
  }
}

const rowEmployerCents = (row) => Number(row.employerCents)
  || (Number(row.profitSharingCents) || 0) + (Number(row.cbCreditCents) || 0)
    + (Number(row.nonelectiveCents) || 0) + (Number(row.matchCents) || 0) + (Number(row.qnecCents) || 0)

function scenarioMetrics(scenario, taxRate, corridor) {
  const rows = scenario?.perPerson || []
  const employerCostCents = Number(scenario?.totals?.employerCostCents)
    || rows.reduce((sum, row) => sum + rowEmployerCents(row), 0)
  const ownerRows = rows.filter((row) => row.role === 'target')
  const ownerBenefitCents = ownerRows.reduce((sum, row) => sum + rowEmployerCents(row), 0)
  const staffCostCents = employerCostCents - ownerBenefitCents
  const cashBalanceCreditCents = rows.reduce((sum, row) => sum + (Number(row.cbCreditCents) || 0), 0)
  const dcEmployerCostCents = employerCostCents - cashBalanceCreditCents
  // A hypothetical CB pay credit is not a deductible contribution. Combined
  // designs use only an actuary-supplied recommended funding amount as the DB
  // tax basis; without it, tax savings remain unavailable rather than guessed.
  const recommendedFundingCents = corridor?.recommended === null || corridor?.recommended === undefined
    ? null : cents(corridor.recommended)
  const estimatedDeductibleContributionCents = cashBalanceCreditCents > 0
    ? (recommendedFundingCents === null ? null : dcEmployerCostCents + recommendedFundingCents)
    : employerCostCents
  const estimatedTaxSavingsCents = estimatedDeductibleContributionCents === null
    ? null : Math.round(estimatedDeductibleContributionCents * taxRate)
  const economicCostBasisCents = estimatedDeductibleContributionCents
  return {
    id: scenario?.id,
    label: scenario?.label,
    category: scenario?.category || (cashBalanceCreditCents > 0 ? 'cash-balance-plus-dc' : 'dc-only'),
    status: scenario?.status?.status ?? 'unavailable',
    staffPercent: Number(scenario?.staffPercent) || 0,
    employerCostCents,
    ownerBenefitCents,
    staffCostCents,
    cashBalanceCreditCents,
    dcEmployerCostCents,
    estimatedDeductibleContributionCents,
    estimatedTaxSavingsCents,
    estimatedAfterTaxCostCents: estimatedDeductibleContributionCents === null
      ? null : estimatedDeductibleContributionCents - estimatedTaxSavingsCents,
    taxSavingsBasis: cashBalanceCreditCents > 0
      ? (recommendedFundingCents === null ? 'requires-actuary-funding' : 'dc-cost-plus-actuary-recommended-funding')
      : 'modeled-dc-employer-cost',
    ownerBenefitSharePercent: employerCostCents > 0 ? ownerBenefitCents / employerCostCents * 100 : 0,
    employerCostPerOwnerBenefitDollar: ownerBenefitCents > 0 && economicCostBasisCents !== null
      ? economicCostBasisCents / ownerBenefitCents : null,
    ownerBenefitPerEmployerDollar: economicCostBasisCents > 0
      ? ownerBenefitCents / economicCostBasisCents : null,
  }
}

function dcOnlyComparisonScenario(scenario) {
  if (!(scenario?.perPerson || []).some((row) => (Number(row.cbCreditCents) || 0) > 0)) return null
  const perPerson = scenario.perPerson.map((row) => {
    const cbCreditCents = Number(row.cbCreditCents) || 0
    return { ...row, cbCreditCents: 0, employerCents: Math.max(0, rowEmployerCents(row) - cbCreditCents) }
  })
  return {
    ...scenario,
    id: `${scenario.id}-dc-only-comparison`,
    label: 'DC-only comparison',
    category: 'dc-only',
    status: { status: 'comparison-only' },
    perPerson,
    totals: {
      ...(scenario.totals || {}),
      employerCostCents: perPerson.reduce((sum, row) => sum + rowEmployerCents(row), 0),
    },
  }
}

function buildSafeHarborAlternatives(participants = []) {
  let basicMatchCents = 0
  let enhancedMatchCents = 0
  let nonelectiveCents = 0
  for (const participant of participants) {
    if (participant.nonexcludableForCoverage === false) continue
    const compCents = cents(participant.allocationCompensation)
    const deferralRate = compCents > 0
      ? ((Number(participant.deferralCents) || 0) / compCents) * 100 : 0
    // Basic safe-harbor match: 100% of first 3%, then 50% of next 2%.
    const basicRate = Math.min(deferralRate, 3) + 0.5 * Math.min(Math.max(deferralRate - 3, 0), 2)
    basicMatchCents += Math.round(compCents * basicRate / 100)
    enhancedMatchCents += Math.round(compCents * Math.min(deferralRate, 4) / 100)
    nonelectiveCents += Math.round(compCents * 0.03)
  }
  return [
    {
      id: 'safe-harbor-basic-match',
      label: 'Basic safe-harbor match',
      employerCostCents: basicMatchCents,
      formula: '100% of the first 3% deferred, plus 50% of the next 2%',
    },
    {
      id: 'safe-harbor-enhanced-match',
      label: 'Enhanced 4% match',
      employerCostCents: enhancedMatchCents,
      formula: '100% of deferrals up to 4% of compensation',
    },
    {
      id: 'safe-harbor-3-percent-nec',
      label: '3% safe-harbor nonelective',
      employerCostCents: nonelectiveCents,
      formula: '3% of compensation for every modeled eligible participant',
    },
  ].map((entry) => ({
    ...entry,
    status: 'cost-comparison-only',
    limitation: 'Cost comparison only. Eligibility, compensation, document timing, top-heavy, and notice requirements require TPA review.',
  }))
}

function buildMinimumParticipationHelper(scenario) {
  const combined = scenario?.detail?.combined
  const minimum = combined?.minimumParticipation
  if (!minimum) return null
  const threshold = Number(minimum.meaningfulAccrualRatePercent) || 0
  const candidates = (combined.rows || [])
    .filter((row) => !row.isHCE && (Number(row.accrualRatePercent) || 0) < threshold)
    .map((row) => ({
      id: row.id,
      currentAccrualRatePercent: Number(row.accrualRatePercent) || 0,
      gapToMeaningfulRatePercent: Math.max(0, threshold - (Number(row.accrualRatePercent) || 0)),
      approximateIncrementCents: Math.max(0, Math.round((Number(row.comp) || 0) * threshold)),
    }))
    .sort((a, b) => a.approximateIncrementCents - b.approximateIncrementCents)
  return {
    passes: minimum.passes === true,
    benefitingCount: minimum.benefitingCount,
    requiredBenefiting: minimum.requiredBenefiting,
    additionalBenefitingNeeded: Math.max(0, minimum.requiredBenefiting - minimum.benefitingCount),
    meaningfulAccrualRatePercent: threshold,
    candidates,
    limitation: 'Candidate ordering is a directional annual-credit proxy. The enrolled actuary must determine a meaningful benefit and adopted participant group.',
  }
}

function buildProjections({ scenario, participants, inputs, planYear, interestRate }) {
  const participantById = new Map(participants.map((person) => [person.id, person]))
  return (scenario?.perPerson || []).map((row) => {
    const person = participantById.get(row.id) || {}
    let openingBalanceCents = Number(person.cashBalanceOpeningBalanceCents) || 0
    let annualCbCreditCents = Number(row.cbCreditCents) || 0
    let annualDcEmployerCents = Math.max(0, rowEmployerCents(row) - annualCbCreditCents)
    let cumulativeDcEmployerCents = 0
    const years = []
    for (let index = 0; index < inputs.projectionYears; index += 1) {
      const interestCreditCents = Math.round(openingBalanceCents * interestRate)
      const endingBalanceCents = openingBalanceCents + interestCreditCents + annualCbCreditCents
      cumulativeDcEmployerCents += annualDcEmployerCents
      years.push({
        year: (Number(planYear) || new Date().getFullYear()) + index,
        openingBalanceCents,
        cashBalanceCreditCents: annualCbCreditCents,
        interestCreditCents,
        endingBalanceCents,
        dcEmployerCents: annualDcEmployerCents,
        cumulativeDcEmployerCents,
      })
      openingBalanceCents = endingBalanceCents
      const growth = 1 + inputs.salaryGrowthPercent / 100
      annualCbCreditCents = Math.round(annualCbCreditCents * growth)
      annualDcEmployerCents = Math.round(annualDcEmployerCents * growth)
    }
    return {
      id: row.id,
      name: person.name || row.name || row.id,
      role: row.role,
      entryDate: person.entryDate || null,
      serviceYears: person.serviceYears ?? null,
      priorVestingYears: person.priorVestingYears ?? null,
      priorAccruedBenefitMonthly: person.priorAccruedBenefitMonthly ?? null,
      openingBalanceCents: Number(person.cashBalanceOpeningBalanceCents) || 0,
      years,
    }
  })
}

export function buildNewCompProposalModel({
  solveResult,
  scenario,
  participants = [],
  reportInputs = {},
  planYear = null,
} = {}) {
  if (!scenario) throw new Error('A scenario is required to build a proposal model.')
  const inputs = normalizeNewCompReportInputs(reportInputs)
  const taxRate = inputs.marginalTaxRatePercent / 100
  const modeledInterest = Number(solveResult?.planFacts?.cashBalance?.interestCreditRate)
  const interestRate = inputs.interestCreditAssumptionPercent === null
    ? (Number.isFinite(modeledInterest) ? modeledInterest : 0.05)
    : inputs.interestCreditAssumptionPercent / 100
  const corridor = inputs.fundingCorridor
  const corridorComplete = [corridor.minimum, corridor.maximum, corridor.recommended]
    .every((value) => value !== null) && Boolean(corridor.source)
  const scenarioPortfolio = (solveResult?.scenarios || [scenario])
    .map((entry) => scenarioMetrics(entry, taxRate, inputs.fundingCorridor))
  const dcOnly = dcOnlyComparisonScenario(scenario)
  return {
    inputs,
    selectedScenarioId: scenario.id,
    selectedMetrics: scenarioMetrics(scenario, taxRate, inputs.fundingCorridor),
    scenarioPortfolio,
    designPortfolio: [
      ...scenarioPortfolio,
      ...(dcOnly ? [scenarioMetrics(dcOnly, taxRate, inputs.fundingCorridor)] : []),
    ],
    safeHarborAlternatives: buildSafeHarborAlternatives(participants),
    projections: buildProjections({ scenario, participants, inputs, planYear, interestRate }),
    projectionAssumptions: {
      years: inputs.projectionYears,
      annualSalaryGrowthPercent: inputs.salaryGrowthPercent,
      annualInterestCreditPercent: interestRate * 100,
      cashBalanceCreditTiming: 'end-of-year',
    },
    fundingCorridor: {
      status: corridorComplete ? 'actuary-supplied' : 'requires-actuary-input',
      minimumCents: corridor.minimum === null ? null : cents(corridor.minimum),
      maximumCents: corridor.maximum === null ? null : cents(corridor.maximum),
      recommendedCents: corridor.recommended === null ? null : cents(corridor.recommended),
      source: corridor.source || null,
      asOf: corridor.asOf || null,
      note: corridor.note || null,
      limitation: corridorComplete
        ? 'Displayed exactly as supplied; the app does not calculate required or deductible funding.'
        : 'Minimum, maximum, and recommended funding must be supplied by the enrolled actuary.',
    },
    minimumParticipationHelper: buildMinimumParticipationHelper(scenario),
    participantDisclosures: {
      ...inputs.disclosures,
      beneficiaryNotice: 'Participants should review beneficiary designations after adoption and after family-status changes.',
      spousalNotice: inputs.disclosures.spousalConsentApplies
        ? 'A married participant may need notarized spousal consent for a non-spouse beneficiary or an optional form of payment.'
        : 'Spousal consent was marked not applicable for this proposal; confirm that conclusion with plan counsel or the TPA.',
      qjsaNotice: inputs.disclosures.qjsaApplies
        ? 'The defined-benefit annuity rules and qualified joint-and-survivor annuity notices must be administered before payment.'
        : 'QJSA was marked not applicable for this proposal; confirm the plan-document basis before reliance.',
    },
  }
}
